import { useEffect, useMemo } from 'react';
import MaterialBreadcrumbs from '@mui/material/Breadcrumbs';
import MuiLink, { LinkProps as MuiLinkProps } from '@mui/material/Link';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link as RouterLink } from 'react-router-dom';
import type { NavigationNode } from '../../graphql/pimcore/modules/siteNavigation/types/NavigationNode';
import { useNavigationNodesUnsafe } from '../../graphql/pimcore/modules/siteNavigation/contexts/NavigationNodesProvider';
import type { ResourceLibraryArticleItem } from '../../graphql/pimcore/modules/resourceLibrary/types/resourceLibraryItems';
import type { ResourceLibrarySitePageItem } from '../../graphql/pimcore/modules/resourceLibrary/types/resourceLibrarySitePageItems';
import {
    breadCrumbs_font,
    breadCrumbs_font2,
} from '../productPage/tableComponents/ProductPage_style';

export interface BreadCrumb {
    name: string;
    route?: string | null;
}

export interface BreadCrumbsProps {
    /** Explicit breadcrumb items; ignored if navigationNode is provided */
    breadCrumbs?: BreadCrumb[];
    /** Navigation node from which to build breadcrumbs */
    navigationNode?: NavigationNode;
    /** Path to get navigation node from; like '/Home/Applications/Automotive/Adas' */
    navigationPath?: string;
}

/**
 * Build an ordered list of breadcrumbs from a navigation node and its parents.
 */
export function buildBreadcrumbsFromNode(node: NavigationNode): BreadCrumb[] {
    const crumbs: BreadCrumb[] = [];
    let current: NavigationNode | undefined = node;
    while (current) {
        crumbs.push({ name: current.name, route: current.route });
        current = current.id !== current.parent.id ? current.parent : undefined;
    }
    return crumbs.reverse();
}

// Linkx component wraps react-router-dom's Link for client-side navigation.
interface LinkxProps extends MuiLinkProps {
    href: string;
    children: React.ReactNode;
}

function Linkx({ href, children, ...muiProps }: LinkxProps) {
    return (
        <MuiLink component={RouterLink} to={href} {...muiProps}>
            {children}
        </MuiLink>
    );
}

export default function BreadCrumbs({
    breadCrumbs = [],
    navigationNode,
    navigationPath,
}: BreadCrumbsProps) {
    const { getNavigationNodeByPath, initialized, initialize, loading } =
        useNavigationNodesUnsafe();

    useEffect(() => {
        // Initialize navigation nodes if not already loaded
        if (!initialized && !navigationNode && navigationPath) {
            initialize();
        }
    }, [navigationNode, navigationPath, initialized, initialize]);

    // get crumbs from navigationNode or navigationPath or breadCrumbs prop
    const crumbs = useMemo<BreadCrumb[]>(() => {
        let node = navigationNode;
        if (!node && navigationPath && initialized && !loading) {
            node = getNavigationNodeByPath(navigationPath);
        }
        return node ? buildBreadcrumbsFromNode(node) : breadCrumbs;
    }, [
        navigationNode,
        breadCrumbs,
        navigationPath,
        getNavigationNodeByPath,
        initialized,
        loading,
    ]);

    return (
        <MaterialBreadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={breadCrumbs_font2}
        >
            {crumbs.map((crumb, index) => {
                const isLast = index === crumbs.length - 1;
                const sxProp = isLast ? breadCrumbs_font2 : breadCrumbs_font;
                if (crumb.route) {
                    const href = crumb.route.startsWith('/') ? crumb.route : `/${crumb.route}`;
                    return (
                        <Linkx key={index} href={href} underline="hover" sx={sxProp}>
                            {crumb.name}
                        </Linkx>
                    );
                }
                return (
                    <MuiLink key={index} underline="none" sx={sxProp}>
                        {crumb.name}
                    </MuiLink>
                );
            })}
        </MaterialBreadcrumbs>
    );
}

interface ResourceLibraryItemBreadCrumbsProps {
    item: ResourceLibraryArticleItem | ResourceLibrarySitePageItem | null;
}

export function ResourceLibraryItemBreadCrumbs({ item }: ResourceLibraryItemBreadCrumbsProps) {
    const { navigationNodeById, initialize } = useNavigationNodesUnsafe();
    if (item?.navigationNodeId) {
        initialize(); // lazy load the navigation nodes only if we need them
    }

    const navigationNode = useMemo<NavigationNode | undefined>(() => {
        if (!item || !item.navigationNodeId) return undefined;
        return navigationNodeById[item.navigationNodeId || ''];
    }, [item, navigationNodeById]);

    return <BreadCrumbs navigationNode={navigationNode} />;
}

export function useBreadcrumbsFromNavigationPath(navigationPath: string | null): BreadCrumb[] {
    const { getNavigationNodeByPath, initialized, initialize, loading } =
        useNavigationNodesUnsafe();

    // init once the provider is present
    useEffect(() => {
        if (!initialized) initialize();
    }, [initialized, initialize]);

    return useMemo<BreadCrumb[]>(() => {
        if (!navigationPath || !initialized || loading) {
            return [];
        }
        const node = getNavigationNodeByPath(navigationPath);
        return node ? buildBreadcrumbsFromNode(node) : [];
    }, [navigationPath, getNavigationNodeByPath, initialized, loading]);
}
